import CheckboxUntyped from "@components/base/checkbox/checkbox";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { createPortal } from "react-dom";

const Checkbox = CheckboxUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

export type CriteriaGroup = { key: string; label: string; options: string[] };
export type CriteriaSelection = Record<string, string[]>;

type CriteriaFilterProps = {
  groups: CriteriaGroup[];
  selection: CriteriaSelection;
  onChange: (selection: CriteriaSelection) => void;
};

const CriteriaFilter = ({ groups, selection, onChange }: CriteriaFilterProps) => {
  const [position, setPosition] = useState<CSSProperties | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const open = position !== null;
  const activeCount = Object.values(selection).reduce((total, values) => total + values.length, 0);

  // The common table clips overflow, so the panel is portalled to <body> and positioned under the icon.
  const toggleOpen = () => {
    if (open || !buttonRef.current) return setPosition(null);
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 8, left: rect.left });
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setPosition(null);
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !buttonRef.current?.contains(target)) close();
    };
    const handleScroll = (event: Event) => {
      if (!panelRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggle = (groupKey: string, option: string) => {
    const current = selection[groupKey] || [];
    const next = current.includes(option) ? current.filter((value) => value !== option) : [...current, option];
    onChange({ ...selection, [groupKey]: next });
  };

  if (groups.length === 0) return null;

  return (
    <div className="tw-relative tw-flex">
      <button ref={buttonRef} type="button" onClick={toggleOpen} aria-label="Filter by recommendation criteria" aria-expanded={open} className={`tw-relative tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded tw-p-0 ${activeCount > 0 || open ? "tw-bg-primary-purple-50 tw-text-primary-purple-600" : "tw-bg-transparent tw-text-gray-900 hover:tw-bg-gray-100"}`}>
        <span className="material-symbols-outlined tw-text-base tw-leading-none">filter_alt</span>
        {activeCount > 0 && <span className="tw-absolute -tw-right-1.5 -tw-top-1.5 tw-flex tw-h-3.5 tw-min-w-3.5 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-purple-500 tw-px-0.5 tw-text-[9px] tw-font-semibold tw-leading-none tw-text-white">{activeCount}</span>}
      </button>
      {open &&
        createPortal(
          <div ref={panelRef} style={position} className="tw-fixed tw-z-[1060] tw-w-[300px] tw-whitespace-normal tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-font-normal tw-shadow-lg">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-3 tw-py-2">
              <span className="tw-text-sm tw-font-medium tw-text-gray-900">Filter by criteria</span>
              <button type="button" onClick={() => onChange({})} disabled={activeCount === 0} className="tw-bg-transparent tw-p-0 tw-text-xs tw-font-medium tw-text-primary-purple-600 disabled:tw-text-gray-400">
                Clear all
              </button>
            </div>
            <div className="tw-max-h-[320px] tw-overflow-y-auto tw-py-1">
              {groups.map((group) => (
                <div key={group.key} className="tw-px-3 tw-py-2">
                  <div className="tw-mb-1.5 tw-text-xs tw-font-medium tw-uppercase tw-text-gray-500">{group.label}</div>
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {group.options.map((option) => (
                      <div key={option} className="tw-text-sm tw-text-gray-900">
                        <Checkbox id={`criteria-${group.key}-${option}`} checked={(selection[group.key] || []).includes(option)} onChange={() => toggle(group.key, option)} label={option} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CriteriaFilter;
